import { cloneDeep, forIn } from "lodash";

import Api from "@/api/Api";
import BaseChart from "@/classes/BaseChart";
import { ALL, DEFAULT_REPORT_DATA_TYPE, NULL_VALUES } from "@/constants";
import { getCountableMetrics, getDimensionMetrics } from "@/helpers/api";
import { setDate } from "@/helpers/dateRangePicker";
import Lang from "@/lang";
import { ProjectService } from "@/services/projectService";
import { API_ROUTES, requestTokens } from "@/settings/apiRoutes";
import { getChartAllSettings } from "@/settings/charts";
import { campaignBudget, dashboardFilters } from "@/settings/dashboardFilters";
import timeMetrics from "@/settings/metrics/timeMetrics";
import store from "@/store";
import { FilterParams } from "@/store/modules/userSettings";
import { ChartDataRecord } from "@/types/charts";
import { DashboardReportFilterParams } from "@/types/dashboard";
import { Project } from "@/types/project";

interface TableRequest {
  data?: Array<Record<string, unknown>>;
  total?: number;
  page?: number;
  next_page?: number;
  page_size?: number;
}

interface ChartApiParams {
  [key: string]: string[] | unknown;
  top?: Record<string, string>;
}

type SummaryDataItem = Record<string, number | null>;

export default class WidgetApi {
  getExploreChartRequestBody(ctx: BaseChart, project?: Project | null) {
    const exploreAdvertiser =
      typeof ctx.settings?.exploreAdvertiser === "string" ? ctx.settings.exploreAdvertiser.trim() : "";
    const dataType = Array.isArray(ctx.settings?.dataType)
      ? ctx.settings.dataType.filter((value): value is string => typeof value === "string" && value.length > 0)
      : [];

    const settingsStart = typeof ctx.settings?.exploreStartDate === "string" ? ctx.settings.exploreStartDate : "";
    const settingsEnd = typeof ctx.settings?.exploreEndDate === "string" ? ctx.settings.exploreEndDate : "";
    const settingsBusinessGoal = Array.isArray(ctx.settings?.exploreBusinessGoal)
      ? ctx.settings.exploreBusinessGoal.filter(
          (value): value is string => typeof value === "string" && value.length > 0
        )
      : [];
    const allBusinessGoals = ((store.getters["catalogs/catalogs"]?.businessGoal as string[] | undefined) ?? []).filter(
      (goal) => goal !== ALL && goal !== NULL_VALUES
    );
    const allBusinessGoalsSelected =
      allBusinessGoals.length > 0 && allBusinessGoals.every((goal) => settingsBusinessGoal.includes(goal));

    const projectFilters = project ? new ProjectService(project).getExploreReportFilters() : null;
    const startTime = settingsStart || projectFilters?.start_time;
    const endTime = settingsEnd || projectFilters?.end_time;
    const businessGoal = allBusinessGoalsSelected
      ? undefined
      : settingsBusinessGoal.length
      ? settingsBusinessGoal
      : projectFilters?.business_goal;
    const advertiser = exploreAdvertiser ? [exploreAdvertiser] : projectFilters?.advertiser;

    const allDataTypes = ((store.getters["catalogs/catalogs"]?.data_type as string[] | undefined) ?? []).filter(
      (type) => type !== ALL && type !== NULL_VALUES
    );
    const allDataTypesSelected = allDataTypes.length > 0 && allDataTypes.every((type) => dataType.includes(type));
    const dataTypeFilter = !dataType.length || allDataTypesSelected ? null : dataType;

    return {
      ...(startTime ? { start_time: startTime } : {}),
      ...(endTime ? { end_time: endTime } : {}),
      ...(businessGoal?.length ? { business_goal: businessGoal } : {}),
      ...(advertiser?.length ? { advertiser } : {}),
      ...(dataTypeFilter ? { data_type: dataTypeFilter } : {}),
      ...this.buildChartApiParams(ctx),
    };
  }

  async getSummaryData(metrics: Array<string>, filter?: FilterParams): Promise<SummaryDataItem[] | undefined> {
    const body = this.getSummaryRequestBody(metrics, filter);

    const res = await Api.request<{ data: SummaryDataItem[] }>({
      path: API_ROUTES.REPORT.PATH,
      defaultResult: { data: [] },
      method: "post",
      body,
      errorText: Lang.errorGetSummaryData,
      token: requestTokens.apiGetSummary,
    });

    return res.data as SummaryDataItem[];
  }

  getSummaryRequestBody(metrics: Array<string>, filter?: FilterParams) {
    const apiParams: Record<string, unknown> = {};
    const filterParams = filter ? this.getFilterParams(filter) : this.getFilterParams();
    const countableMetrics = getCountableMetrics(metrics);
    if (countableMetrics) {
      apiParams.count_metrics = countableMetrics;
    }
    return {
      ...filterParams,
      ...apiParams,
    };
  }

  async getChartData(
    ctx: BaseChart,
    filterParamsTransformer?: (params: DashboardReportFilterParams) => DashboardReportFilterParams
  ): Promise<ChartDataRecord[]> {
    const body = this.getChartRequestBody(ctx, filterParamsTransformer);
    const res = await Api.request<{ data: ChartDataRecord[] }>({
      path: API_ROUTES.REPORT.PATH,
      defaultResult: { data: [] },
      method: "post",
      body,
      noDataItem: { widgetId: ctx.widget.id, status: true },
      errorText: Lang.errorGetData,
      token: requestTokens.apiGetChartData,
    });
    return res.data;
  }

  getChartRequestBody(
    ctx: BaseChart,
    filterParamsTransformer?: (params: DashboardReportFilterParams) => DashboardReportFilterParams
  ) {
    const filterParams = filterParamsTransformer
      ? filterParamsTransformer(this.getFilterParams())
      : this.getFilterParams();
    return {
      ...filterParams,
      ...this.buildChartApiParams(ctx),
    };
  }

  getFilterParams(filterParams = store.getters["userSettings/filterParams"]): DashboardReportFilterParams {
    const normalizedParams = {} as DashboardReportFilterParams;
    const selectedFilters = store.getters["userSettings/selectedFilters"] as string[];
    const isDataTypeFilterSelected = selectedFilters.includes("data_type");

    for (const param in filterParams) {
      if (Array.isArray(filterParams[param]) && !filterParams[param].includes("All")) {
        const apiKey = dashboardFilters.find((filter) => {
          return filter.filterKey === param;
        })?.apiKey;

        if (apiKey) {
          normalizedParams[apiKey] = cloneDeep(filterParams[param]);
        }
      }
    }

    if (!isDataTypeFilterSelected) {
      normalizedParams.data_type = [DEFAULT_REPORT_DATA_TYPE];
    }

    if (filterParams.dateRange.type === "basic") {
      normalizedParams.start_time = filterParams.dateRange.value?.startTime;
      normalizedParams.end_time = filterParams.dateRange.value?.endTime;
    } else {
      const { startDate, endDate } = setDate(filterParams.dateRange.value?.preset as string);
      if (startDate && endDate) {
        normalizedParams.start_time = startDate;
        normalizedParams.end_time = endDate;
      }
    }

    if (filterParams.campaignBudget && !filterParams.campaignBudget.includes(Lang.all)) {
      normalizedParams.budgets = filterParams.campaignBudget
        .map((item: string) => {
          return campaignBudget.find((campaign) => campaign.text === item)?.value;
        })
        .filter((item: Record<string, number> | undefined) => !!item);
    }

    return normalizedParams;
  }

  buildChartApiParams(ctx: BaseChart): ChartApiParams {
    const apiParams = {} as ChartApiParams;
    const allSettings = getChartAllSettings(ctx.chartName, ctx.chartFamily);
    const showItems = ctx.settings?.showItems || null;
    let dimension = "";
    let metric = "";

    forIn(allSettings, (setting: Record<string, string | number | number[]>, key: string) => {
      if (setting.dictionary === "displayMetrics") {
        if (["xAxis", "yAxis", "xAxisPrimary", "yAxisPrimary", "displayMetric"].includes(key)) {
          metric = ctx.settings[key];
        }
      }

      if (setting.apiParameter) {
        const value = ctx.settings[key];
        const decompositionMetric = key === "decompositionMetricTop" ? "decompositionMetricTop" : "decompositionMetric";

        if (["yAxis", "xAxis", "xAxisPrimary", "yAxisPrimary", decompositionMetric].includes(key)) {
          dimension = value;
        }

        if (Array.isArray(apiParams[setting.apiParameter as string])) {
          (apiParams[setting.apiParameter as string] as string[]).push(value);
        } else {
          apiParams[setting.apiParameter as string] = [value];
        }
      }
    });

    if (!metric && ctx.settings.lineMetric) {
      metric = ctx.settings.lineMetric as string;
    }

    if (showItems && dimension && !timeMetrics.find((item) => item.value === dimension)) {
      apiParams.top = {
        count: showItems,
        dimension,
        metric,
      };
    }

    const countableMetrics = getCountableMetrics(Object.values(ctx.settings));

    if (countableMetrics) {
      apiParams.count_metrics = countableMetrics;
    }

    return apiParams;
  }

  async getTableData(filter: FilterParams, selectedMetrics: string[]): Promise<TableRequest | undefined> {
    const body = this.getTableRequestBody(filter, selectedMetrics);

    return await Api.request<TableRequest>({
      path: API_ROUTES.REPORT.PATH,
      defaultResult: { data: [] },
      method: "post",
      body,
      errorText: Lang.errorGetTableData,
      token: requestTokens.apiGetTableData,
    });
  }

  getTableRequestBody(filter: FilterParams, selectedMetrics: string[]) {
    const apiParams: Record<string, unknown> = {};
    const filterParams = filter ? this.getFilterParams(filter) : this.getFilterParams();
    const countableMetrics = getCountableMetrics(selectedMetrics);

    if (countableMetrics) {
      apiParams.count_metrics = countableMetrics;
    }

    apiParams.dimension = getDimensionMetrics(selectedMetrics);

    return { ...filterParams, ...apiParams };
  }
}
